import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm } from 'redux-form';

import { SHIFT_SETTINGS } from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import * as CompanyActions from 'af-actions/companies';

import { ShiftSettingsFM, validate } from './formModel';
import ShiftSettingsForm from './ShiftSettingsForm';

type FormProps = InjectedFormProps<ShiftSettingsFM, FormOwnProps>;
type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;

type OwnProps = {
	showModal: boolean;
	closeModal: () => void;
	initialValues: FormProps['initialValues'];
};

type Props = OwnProps & ConnectedProps<typeof connector> & InjectedFormProps<ShiftSettingsFM>;

const ShiftSettingsEdit: React.FC<Props> = (props) => {

	const { closeModal, showModal, editShiftSettings, handleSubmit, reset, invalid } = props;

	const handleClose = React.useCallback(() => {
		closeModal();
		reset();
	}, [closeModal, reset]);

	const onSubmit = React.useCallback(async (form: ShiftSettingsFM) => {
		await editShiftSettings(ShiftSettingsFM.toRM(form));
		closeModal();
	}, [closeModal, editShiftSettings]);

	return (
		<CustomModal
			closeModal={handleClose}
			modalStyle="info"
			showModal={showModal}
			size="lg"
		>
			<CustomModal.Header
				closeModal={handleClose}
				title="Edit Shift Settings"
			/>
			<CustomModal.Body>
				<ShiftSettingsForm
					{...props}
					onSubmit={onSubmit}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={handleClose}
					style="secondary"
				/>
				<SubmitButton
					disabled={invalid}
					label="Save"
					onClick={handleSubmit(onSubmit)}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapDispatchToProps() {
	return {
		editShiftSettings: CompanyActions.editShiftSettings,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	connector,
	reduxForm({ form: SHIFT_SETTINGS, enableReinitialize: true, validate })
);

export default enhance(ShiftSettingsEdit);
